import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  Plus,
  Trash2,
  Sparkles,
  Loader2,
  Edit3,
  Check,
  X,
  Smile,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { analyzeText } from '@/lib/ai';
import {
  createRecipe,
  updateFavoriteBasic,
  updateRecipeIngredients,
  getFavoriteWithIngredients,
} from '@/hooks/useFavorites';
import { fmtNum, mealTypeLabel } from '@/utils/format';
import type { MealType, FavoriteIngredient } from '@/types';

interface IngredientDraft {
  // Локальный id для key/удаления (не сохраняется в БД)
  localId: string;
  name: string;
  grams: number;
  calories: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;

  // 🆕 БАЗОВЫЕ значения — на исходный вес (не меняются при изменении граммовки)
  // Используются для пропорционального пересчёта. Хранятся только в локальном стейте,
  // не сохраняются в БД (в БД идут уже пересчитанные текущие значения).
  base_grams: number;
  base_calories: number;
  base_protein_g: number;
  base_fat_g: number;
  base_carbs_g: number;
}

interface RecipeFormProps {
  /** Если задано — режим редактирования существующего рецепта */
  recipeId?: string;
  /** Callback после успешного сохранения */
  onSaved?: (recipeId: string) => void;
  /** Callback при отмене */
  onCancel?: () => void;
}

const EMOJI_PRESETS = [
  '🍲', '🍜', '🍝', '🥘', '🍛', '🥣', '🍳', '🥞',
  '🥗', '🌮', '🌯', '🥪', '🍔', '🍕', '🍱', '🍘',
  '🥩', '🍗', '🐟', '🦐', '🥚', '🧀',
  '🍎', '🍌', '🍓', '🥥', '🥑', '🥕',
  '☕', '🥤', '🍵', '🥛',
];

const RECIPE_CATEGORIES: { value: MealType | null; label: string; emoji: string }[] = [
  { value: null, label: 'Любой', emoji: '🍽' },
  { value: 'breakfast', label: 'Завтрак', emoji: '🍳' },
  { value: 'lunch', label: 'Обед', emoji: '🥘' },
  { value: 'dinner', label: 'Ужин', emoji: '🌙' },
  { value: 'snack', label: 'Перекус', emoji: '🍎' },
];

function makeId(): string {
  return Math.random().toString(36).slice(2, 11);
}

export function RecipeForm({ recipeId, onSaved, onCancel }: RecipeFormProps) {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const isEditing = !!recipeId;
  // Основные поля
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🍲');
  const [category, setCategory] = useState<MealType | null>(null);
  const [totalPortions, setTotalPortions] = useState<string>('1');
  const [portionLabel, setPortionLabel] = useState('порция');
  const [notes, setNotes] = useState('');
  // Ингредиенты
  const [ingredients, setIngredients] = useState<IngredientDraft[]>([]);
  // Ввод нового ингредиента
  const [newIngredientText, setNewIngredientText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  // Ручной ввод
  const [manualName, setManualName] = useState('');
  const [manualGrams, setManualGrams] = useState('100');
  const [manualCalories, setManualCalories] = useState('');
  const [manualProtein, setManualProtein] = useState('');
  const [manualFat, setManualFat] = useState('');
  const [manualCarbs, setManualCarbs] = useState('');
  // Состояния
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // Загрузка существующего рецепта (для редактирования)
  useEffect(() => {
    if (!recipeId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await getFavoriteWithIngredients(recipeId);
      if (cancelled) return;
      if (result) {
        const { favorite, ingredients: ings } = result;
        setName(favorite.name);
        setEmoji(favorite.emoji || '🍲');
        setCategory(favorite.category ?? null);
        setTotalPortions(String(favorite.total_portions ?? 1));
        setPortionLabel(favorite.portion_label ?? 'порция');
        setNotes(favorite.notes ?? '');
        setIngredients(
          ings.map((i: FavoriteIngredient) => {
            // 🆕 При загрузке из БД: текущие значения = базовые
            const grams = Number(i.grams) || 0;
            const calories = Number(i.calories) || 0;
            const protein_g = Number(i.protein_g) || 0;
            const fat_g = Number(i.fat_g) || 0;
            const carbs_g = Number(i.carbs_g) || 0;
            return {
              localId: i.id,
              name: i.name,
              grams,
              calories,
              protein_g,
              fat_g,
              carbs_g,
              // 🆕 Сохраняем базовые значения для пересчёта
              base_grams: grams,
              base_calories: calories,
              base_protein_g: protein_g,
              base_fat_g: fat_g,
              base_carbs_g: carbs_g,
            };
          })
        );
      } else {
        alert('Не удалось загрузить рецепт');
        if (onCancel) onCancel();
        else nav(-1);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [recipeId, onCancel, nav]);
  // Расчёт общего КБЖУ
  const totals = useMemo(() => {
    return ingredients.reduce(
      (acc, ing) => ({
        calories: acc.calories + (Number(ing.calories) || 0),
        protein_g: acc.protein_g + (Number(ing.protein_g) || 0),
        fat_g: acc.fat_g + (Number(ing.fat_g) || 0),
        carbs_g: acc.carbs_g + (Number(ing.carbs_g) || 0),
        grams: acc.grams + (Number(ing.grams) || 0),
      }),
      { calories: 0, protein_g: 0, fat_g: 0, carbs_g: 0, grams: 0 }
    );
  }, [ingredients]);
  // КБЖУ на 1 порцию
  const perPortion = useMemo(() => {
    const portions = Number(totalPortions) || 1;
    if (portions <= 0) return null;
    return {
      calories: Math.round(totals.calories / portions),
      protein_g: +(totals.protein_g / portions).toFixed(1),
      fat_g: +(totals.fat_g / portions).toFixed(1),
      carbs_g: +(totals.carbs_g / portions).toFixed(1),
      weight: Math.round(totals.grams / portions),
    };
  }, [totals, totalPortions]);
  // Добавить ингредиент через AI
  async function handleAddViaAI() {
    if (!newIngredientText.trim()) return;
    setAnalyzing(true);
    haptic('medium');
    try {
      const result = await analyzeText(newIngredientText.trim());
      if (!result.items?.length) {
        haptic('error');
        alert('Не удалось распознать. Попробуй переформулировать.');
        return;
      }
      // Добавляем ВСЕ распознанные продукты как ингредиенты
      const newIngs: IngredientDraft[] = result.items.map((item) => {
        const grams = Number(item.estimated_weight_g) || 100;
        const calories = Number(item.calories) || 0;
        const protein_g = Number(item.protein_g) || 0;
        const fat_g = Number(item.fat_g) || 0;
        const carbs_g = Number(item.carbs_g) || 0;
        return {
          localId: makeId(),
          name: item.name,
          grams,
          calories,
          protein_g,
          fat_g,
          carbs_g,
          // 🆕 Базовые значения = текущие при добавлении
          base_grams: grams,
          base_calories: calories,
          base_protein_g: protein_g,
          base_fat_g: fat_g,
          base_carbs_g: carbs_g,
        };
      });
      setIngredients((prev) => [...prev, ...newIngs]);
      setNewIngredientText('');
      haptic('success');
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось распознать. Попробуй ещё раз.');
    } finally {
      setAnalyzing(false);
    }
  }
  // Добавить ингредиент вручную
  function handleAddManual() {
    if (!manualName.trim() || !manualCalories) {
      alert('Заполни хотя бы название и калории');
      return;
    }
    haptic('light');
    const grams = Number(manualGrams) || 100;
    const calories = Number(manualCalories) || 0;
    const protein_g = Number(manualProtein) || 0;
    const fat_g = Number(manualFat) || 0;
    const carbs_g = Number(manualCarbs) || 0;
    setIngredients((prev) => [
      ...prev,
      {
        localId: makeId(),
        name: manualName.trim(),
        grams,
        calories,
        protein_g,
        fat_g,
        carbs_g,
        // 🆕 Базовые значения = введённые
        base_grams: grams,
        base_calories: calories,
        base_protein_g: protein_g,
        base_fat_g: fat_g,
        base_carbs_g: carbs_g,
      },
    ]);
    // Сброс полей
    setManualName('');
    setManualGrams('100');
    setManualCalories('');
    setManualProtein('');
    setManualFat('');
    setManualCarbs('');
    setShowManualInput(false);
  }
  function handleRemoveIngredient(localId: string) {
    haptic('light');
    setIngredients((prev) => prev.filter((i) => i.localId !== localId));
  }

  // 🆕 ИСПРАВЛЕННАЯ ФУНКЦИЯ — пересчёт всегда от base_*, а не от текущих
  // Это решает баг обнуления: если юзер вводит 0 (пустое поле),
  // КБЖУ временно становятся 0, но при следующем вводе пересчитываются от исходного веса.
  function handleUpdateIngredientGrams(localId: string, newGrams: number) {
    setIngredients((prev) =>
      prev.map((i) => {
        if (i.localId !== localId) return i;

        // Защита: если base_grams == 0 (что почти невозможно), используем 1
        const baseGrams = i.base_grams || 1;
        const ratio = newGrams / baseGrams;

        return {
          ...i,
          grams: newGrams,
          calories: Math.round(i.base_calories * ratio),
          protein_g: +(i.base_protein_g * ratio).toFixed(1),
          fat_g: +(i.base_fat_g * ratio).toFixed(1),
          carbs_g: +(i.base_carbs_g * ratio).toFixed(1),
          // base_* остаются нетронутыми — они "якорь" для пересчёта
        };
      })
    );
  }
  // Сохранение рецепта
  async function handleSave() {
    if (!name.trim()) {
      alert('Введи название рецепта');
      return;
    }
    if (!ingredients.length) {
      alert('Добавь хотя бы один ингредиент');
      return;
    }
    const portions = Number(totalPortions) || 1;
    if (portions <= 0) {
      alert('Количество порций должно быть больше 0');
      return;
    }
    setSaving(true);
    haptic('medium');
    try {
      const ingredientsToSave = ingredients.map((i) => ({
        name: i.name,
        grams: i.grams,
        calories: i.calories,
        protein_g: i.protein_g,
        fat_g: i.fat_g,
        carbs_g: i.carbs_g,
      }));
      if (isEditing && recipeId) {
        // РЕДАКТИРОВАНИЕ
        const basicResult = await updateFavoriteBasic(recipeId, {
          name: name.trim(),
          emoji,
          category,
          notes: notes.trim() || undefined,
          portion_label: portionLabel,
        });
        if (!basicResult.success) {
          throw new Error(basicResult.error ?? 'Ошибка обновления');
        }
        const ingsResult = await updateRecipeIngredients(
          recipeId,
          portions,
          ingredientsToSave
        );
        if (!ingsResult.success) {
          throw new Error(ingsResult.error ?? 'Ошибка обновления ингредиентов');
        }
        haptic('success');
        onSaved?.(recipeId);
        if (!onSaved) nav(-1);
      } else {
        // СОЗДАНИЕ НОВОГО
        const result = await createRecipe({
          name: name.trim(),
          emoji,
          category,
          total_portions: portions,
          portion_label: portionLabel,
          notes: notes.trim() || undefined,
          ingredients: ingredientsToSave,
        });
        if (!result.success || !result.recipe) {
          throw new Error(result.error ?? 'Ошибка создания');
        }
        haptic('success');
        onSaved?.(result.recipe.id);
        if (!onSaved) nav(-1);
      }
    } catch (e: any) {
      console.error(e);
      haptic('error');
      alert(`Не удалось сохранить: ${e.message ?? 'ошибка'}`);
    } finally {
      setSaving(false);
    }
  }
  if (loading) {
    return (
      <div className="px-5 pt-6 pb-28 max-w-md mx-auto flex items-center justify-center min-h-[50vh]">
        <Loader2 size={28} className="animate-spin text-emerald-glow" />
      </div>
    );
  }
  return (
    <div className="px-5 pt-4 pb-32 max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => (onCancel ? onCancel() : nav(-1))}
          className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="font-display text-xl font-bold flex-1">
          {isEditing ? 'Редактировать рецепт' : 'Новый рецепт'}
        </h1>
      </div>
      {/* Эмодзи + Название */}
      <div className="card-elevated p-4 mb-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => {
              setShowEmojiPicker(!showEmojiPicker);
              haptic('light');
            }}
            className="w-16 h-16 rounded-2xl bg-bg-elevated flex items-center justify-center text-3xl hover:bg-line/40 active:scale-95 transition-all shrink-0"
          >
            {emoji}
          </button>
          <div className="flex-1 min-w-0">
            <label className="label mb-2 block">Название</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Мамин борщ"
              className="input"
              maxLength={50}
            />
          </div>
        </div>
        <AnimatePresence>
          {showEmojiPicker && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-4 grid grid-cols-8 gap-1.5">
                {EMOJI_PRESETS.map((e) => (
                  <button
                    type="button"
                    key={e}
                    onClick={() => {
                      setEmoji(e);
                      setShowEmojiPicker(false);
                      haptic('light');
                    }}
                    className={`w-9 h-9 rounded-lg text-xl flex items-center justify-center transition-colors ${
                      emoji === e
                        ? 'bg-emerald-glow/20 ring-1 ring-emerald-glow'
                        : 'hover:bg-bg-elevated/60'
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Категория */}
      <div className="mb-4">
        <label className="label mb-2 block">Категория (для сортировки)</label>
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
          {RECIPE_CATEGORIES.map((cat) => (
            <button
              type="button"
              key={cat.value ?? 'any'}
              onClick={() => {
                setCategory(cat.value);
                haptic('light');
              }}
              className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                category === cat.value
                  ? 'bg-emerald-glow text-bg'
                  : 'bg-bg-card text-ink-muted border border-line'
              }`}
            >
              <span>{cat.emoji}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Ингредиенты */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-2">
          <label className="label">Ингредиенты ({ingredients.length})</label>
          {ingredients.length > 0 && (
            <div className="text-[11px] text-ink-dim">
              Всего: {fmtNum(totals.grams)}г
            </div>
          )}
        </div>
        {/* Список ингредиентов */}
        {ingredients.length > 0 && (
          <div className="space-y-2 mb-3">
            {ingredients.map((ing) => (
              <IngredientCard
                key={ing.localId}
                ingredient={ing}
                onRemove={() => handleRemoveIngredient(ing.localId)}
                onUpdateGrams={(g) => handleUpdateIngredientGrams(ing.localId, g)}
              />
            ))}
          </div>
        )}
        {/* AI ввод */}
        {!showManualInput && (
          <div className="card p-3">
            <label className="label mb-2 block">Добавить через ИИ</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newIngredientText}
                onChange={(e) => setNewIngredientText(e.target.value)}
                placeholder="например: говядина 500г"
                className="input flex-1 text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !analyzing) handleAddViaAI();
                }}
              />
              <Button
                size="md"
                onClick={handleAddViaAI}
                disabled={!newIngredientText.trim() || analyzing}
                icon={
                  analyzing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Sparkles size={14} />
                  )
                }
              >
                {analyzing ? '…' : 'Добавить'}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowManualInput(true);
                haptic('light');
              }}
              className="text-[11px] text-ink-dim hover:text-emerald-glow transition-colors flex items-center gap-1"
            >
              <Edit3 size={10} /> Ввести вручную (без ИИ)
            </button>
          </div>
        )}
        {/* Ручной ввод */}
        {showManualInput && (
          <div className="card p-3">
            <div className="flex items-center justify-between mb-2">
              <label className="label">Ингредиент вручную</label>
              <button
                type="button"
                onClick={() => {
                  setShowManualInput(false);
                  haptic('light');
                }}
                className="text-ink-dim text-xs hover:text-ink"
              >
                ← Вернуться к ИИ
              </button>
            </div>
            <input
              className="input mb-2 text-sm"
              placeholder="Название"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="text-[10px] text-ink-dim block mb-1">Вес, г</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="input text-sm py-1.5 px-2"
                  value={manualGrams}
                  onChange={(e) => setManualGrams(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div>
                <label className="text-[10px] text-ink-dim block mb-1">Калории</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="ккал"
                  className="input text-sm py-1.5 px-2"
                  value={manualCalories}
                  onChange={(e) => setManualCalories(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div>
                <label className="text-[10px] text-emerald-glow block mb-1">Б, г</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="input text-sm py-1.5 px-2"
                  placeholder="0"
                  value={manualProtein}
                  onChange={(e) =>
                    setManualProtein(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))
                  }
                />
              </div>
              <div>
                <label className="text-[10px] text-warn block mb-1">Ж, г</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="input text-sm py-1.5 px-2"
                  placeholder="0"
                  value={manualFat}
                  onChange={(e) =>
                    setManualFat(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))
                  }
                />
              </div>
              <div>
                <label className="text-[10px] text-sky-glow block mb-1">У, г</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="input text-sm py-1.5 px-2"
                  placeholder="0"
                  value={manualCarbs}
                  onChange={(e) =>
                    setManualCarbs(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))
                  }
                />
              </div>
            </div>
            <Button
              fullWidth
              size="sm"
              variant="secondary"
              onClick={handleAddManual}
              disabled={!manualName.trim() || !manualCalories}
              icon={<Plus size={14} />}
            >
              Добавить ингредиент
            </Button>
          </div>
        )}
      </div>
      {/* Количество порций */}
      <div className="mb-4">
        <label className="label mb-2 block">Количество порций</label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={totalPortions}
            onChange={(e) => setTotalPortions(e.target.value.replace(/\D/g, ''))}
            className="input text-center font-display text-lg font-bold"
          />
          <input
            type="text"
            value={portionLabel}
            onChange={(e) => setPortionLabel(e.target.value)}
            placeholder="порция / миска / кусок"
            className="input text-sm"
            maxLength={20}
          />
        </div>
      </div>
      {/* Заметки */}
      <div className="mb-4">
        <label className="label mb-2 block">Заметки (опционально)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Например: делать на воде, без сметаны"
          className="input min-h-[60px] resize-none text-sm"
          maxLength={200}
        />
      </div>
      {/* Итого */}
      {ingredients.length > 0 && perPortion && (
        <div className="card-elevated p-4 border-emerald-glow/30 mb-4">
          <div className="text-[11px] text-ink-dim uppercase tracking-wider mb-2">
            ⚡ ИИ посчитал
          </div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="label">Всего</span>
            <span className="font-display text-lg font-bold">
              {fmtNum(totals.calories)} ккал
            </span>
          </div>
          <div className="flex gap-3 text-xs mb-3">
            <span className="text-emerald-glow">Б {fmtNum(totals.protein_g, 1)}г</span>
            <span className="text-warn">Ж {fmtNum(totals.fat_g, 1)}г</span>
            <span className="text-sky-glow">У {fmtNum(totals.carbs_g, 1)}г</span>
          </div>
          <div className="pt-3 border-t border-line/40">
            <div className="flex items-baseline justify-between mb-1">
              <span className="label">1 {portionLabel}</span>
              <span className="font-display text-2xl font-bold text-emerald-glow">
                {fmtNum(perPortion.calories)} ккал
              </span>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="text-emerald-glow">Б {fmtNum(perPortion.protein_g, 1)}г</span>
              <span className="text-warn">Ж {fmtNum(perPortion.fat_g, 1)}г</span>
              <span className="text-sky-glow">У {fmtNum(perPortion.carbs_g, 1)}г</span>
              {perPortion.weight > 0 && (
                <span className="text-ink-dim ml-auto">{perPortion.weight}г</span>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Sticky кнопка сохранить */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-16 left-0 right-0 z-40 px-5 pb-3 pt-3 bg-gradient-to-t from-bg via-bg/95 to-bg/0 max-w-md mx-auto"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
      >
        <Button
          fullWidth
          size="lg"
          disabled={saving || !name.trim() || ingredients.length === 0}
          onClick={handleSave}
          icon={saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
          className="shadow-2xl"
        >
          {saving
            ? 'Сохраняем…'
            : isEditing
              ? 'Сохранить изменения'
              : 'Сохранить рецепт'}
        </Button>
      </motion.div>
    </div>
  );
}
// ============================================
// КАРТОЧКА ИНГРЕДИЕНТА (sub-component)
// ============================================
function IngredientCard({
  ingredient,
  onRemove,
  onUpdateGrams,
}: {
  ingredient: IngredientDraft;
  onRemove: () => void;
  onUpdateGrams: (g: number) => void;
}) {
  // 🆕 Локальный стейт для строкового значения поля ввода
  // Позволяет временно держать пустую строку без обнуления данных
  const [gramsInput, setGramsInput] = useState(String(ingredient.grams));

  // Синхронизируем при изменении ingredient.grams снаружи
  useEffect(() => {
    setGramsInput(String(ingredient.grams));
  }, [ingredient.grams]);

  return (
    <div className="card p-3">
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink truncate">{ingredient.name}</div>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={gramsInput}
              onChange={(e) => {
                // 🆕 Просто обновляем строку — не пересчитываем сразу
                const cleaned = e.target.value.replace(/\D/g, '');
                setGramsInput(cleaned);
              }}
              onBlur={() => {
                // 🆕 Пересчитываем только при потере фокуса
                const v = Number(gramsInput) || 0;
                if (v > 0) {
                  onUpdateGrams(v);
                } else {
                  // Если ввели 0 или пусто — возвращаем предыдущее значение
                  setGramsInput(String(ingredient.grams));
                }
              }}
              onKeyDown={(e) => {
                // 🆕 По Enter — пересчитываем
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="input py-1 px-2 w-20 text-xs"
            />
            <span className="text-[11px] text-ink-dim">г</span>
            <span className="ml-auto text-xs font-semibold">
              {fmtNum(ingredient.calories)} ккал
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="w-7 h-7 rounded-md text-ink-dim hover:text-danger hover:bg-danger/10 flex items-center justify-center transition-colors"
        >
          <Trash2 size={13} />
        </button>
      </div>
      <div className="flex gap-3 text-[10px] pl-1">
        <span className="text-emerald-glow">Б {fmtNum(ingredient.protein_g, 1)}</span>
        <span className="text-warn">Ж {fmtNum(ingredient.fat_g, 1)}</span>
        <span className="text-sky-glow">У {fmtNum(ingredient.carbs_g, 1)}</span>
      </div>
    </div>
  );
}
